/**
 * `schemas/playlist.ts` — `spec.md` §6.6, the playlist half.
 *
 * The one subtle shape here is `position`: `playlist_items.position` is a `REAL` so a
 * reorder is a single UPDATE to the midpoint of two neighbours, and the server
 * renumbers the whole playlist when the gap falls below `POSITION_EPSILON` (1e-6).
 * That is why `PlaylistPositionEntry.position` is a plain float and not an integer.
 */
import * as z from 'zod';
import { AckSchema, CursorSchema, NumericIdParams, PositiveIntParam, ShareSlug, ShortId, TrackId, UnixSeconds, limitParam, } from './common.js';
import { TrackPageSchema } from './track.js';
/** §6.6: "cap 200 per user, enforced with `409 CONFLICT` on create beyond it". */
export const PLAYLIST_MAX_PER_USER = 200;
/** §6.6: `title: string(1..80)`. */
export const PlaylistTitle = z.string().min(1).max(80);
/**
 * The playlist header — what `GET /api/playlists` lists and what the mutating endpoints
 * return so the client can reconcile `trackCount`/`durationS` without a refetch.
 *
 * `shareSlug` is `null` unless `isPublic`: §6.6 requires that setting `isPublic: false`
 * CLEARS the slug, because "an unshared link must die".
 */
export const PlaylistHeaderSchema = z.strictObject({
    id: z.int().positive(),
    shortId: ShortId,
    title: PlaylistTitle,
    trackCount: z.int().nonnegative(),
    durationS: z.int().nonnegative(),
    isPublic: z.boolean(),
    shareSlug: ShareSlug.nullable(),
    updatedAt: UnixSeconds,
});
/** §6.6: "No pagination (cap 200 per user…)". */
export const PlaylistsResponseSchema = z.strictObject({
    items: z.array(PlaylistHeaderSchema).max(PLAYLIST_MAX_PER_USER),
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/playlists
 * ──────────────────────────────────────────────────────────────────────────── */
export const PlaylistCreateRequestSchema = z.strictObject({
    title: PlaylistTitle,
    trackIds: z.array(TrackId).max(500).optional(),
});
/** `201` with the playlist object. */
export const PlaylistCreateResponseSchema = PlaylistHeaderSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/playlists/:id
 * ──────────────────────────────────────────────────────────────────────────── */
export const PlaylistParamsSchema = NumericIdParams;
export const PlaylistTracksQuerySchema = z.object({
    limit: limitParam(1, 200, 60),
    cursor: CursorSchema.optional(),
});
export const PlaylistDetailResponseSchema = z.strictObject({
    playlist: PlaylistHeaderSchema,
    tracks: TrackPageSchema,
});
/* ────────────────────────────────────────────────────────────────────────────
 * PATCH /api/playlists/:id
 * ──────────────────────────────────────────────────────────────────────────── */
/** A PATCH with neither field is a no-op the client should not have sent. */
export const PlaylistPatchRequestSchema = z
    .strictObject({
    title: PlaylistTitle.optional(),
    isPublic: z.boolean().optional(),
})
    .refine((body) => body.title !== undefined || body.isPublic !== undefined, {
    error: 'a patch must change at least one of title or isPublic',
});
export const PlaylistPatchResponseSchema = PlaylistHeaderSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/playlists/:id/tracks
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `"start" | "end" | number`. A number inserts BEFORE that 0-based index, so `0` is
 * equivalent to `"start"` and any index at or past the end is equivalent to `"end"`.
 */
export const PlaylistInsertPosition = z.union([z.enum(['start', 'end']), z.int().nonnegative()]);
export const PlaylistAddTracksRequestSchema = z.strictObject({
    trackIds: z.array(TrackId).min(1).max(200),
    position: PlaylistInsertPosition.optional(),
});
/** §6.6: "Returns the updated playlist header." */
export const PlaylistAddTracksResponseSchema = PlaylistHeaderSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * DELETE /api/playlists/:id/tracks/:trackId
 * ──────────────────────────────────────────────────────────────────────────── */
export const PlaylistTrackParamsSchema = z.object({
    id: PositiveIntParam,
    trackId: PositiveIntParam,
});
/**
 * §6.6 gives the status (`200`) and nothing about the body, so this is the shared
 * minimal ack rather than an invented payload.
 */
export const PlaylistRemoveTrackResponseSchema = AckSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/playlists/:id/reorder
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `beforeTrackId: null` means "move to the end". The server computes the midpoint of
 * the neighbours; a client never sends a position.
 */
export const PlaylistReorderRequestSchema = z
    .strictObject({
    trackId: TrackId,
    beforeTrackId: TrackId.nullable(),
})
    .refine((body) => body.beforeTrackId !== body.trackId, {
    error: 'a track cannot be reordered before itself',
    path: ['beforeTrackId'],
});
/**
 * `position` is a `REAL`. The response carries EVERY affected row, because a renumber
 * (triggered when the midpoint gap drops under 1e-6) rewrites the whole playlist and a
 * client that only patched the moved row would be out of sync.
 */
export const PlaylistPositionEntrySchema = z.strictObject({
    trackId: TrackId,
    position: z.number(),
});
export const PlaylistReorderResponseSchema = z.strictObject({
    positions: z.array(PlaylistPositionEntrySchema),
});
//# sourceMappingURL=playlist.js.map